import IconTips from '@sg/components/IconTips';
import React from 'react';
import { sanitizeRichHtml } from '@sg/utils/sanitizeHtml';

// title渲染
export const height = '443px';
export const rendTitle = (data: any) => {
  const headerStyle: React.CSSProperties = {};
  if (data?.align) {
    headerStyle.justifyContent = data.align;
  }

  return (
    <>
      <div className="dbo-card-title dbs-card-title" style={headerStyle}>
        <span className="dbo-card-titles" style={{ fontSize: data?.titleSize, fontWeight: data?.fontWeight }}>
          {data?.title}
        </span>
        {data?.tipString && <IconTips tipString={data?.tipString} />}
        {data?.tips && (
          <span className="dbo-card-title-sub" style={{ fontSize: data?.tipSize }}>
            {data?.tips}
          </span>
        )}
      </div>
    </>
  );
};

/**
 * Renders rich text content (typically from TinyMCE) with HTML support.
 * Relies on CSS (.dbs-rich-text-wrapper) to handle <p> tag spacing
 * instead of converting newlines to <br/> tags.
 * @param value - The HTML string to render
 * @param className - Optional additional CSS class name to apply
 * @returns React element with rendered HTML or a dash for empty values
 */
export const renderRichText = (value?: string, className?: string) => {
  if (!value || value.trim() === '') {
    return <span>-</span>;
  }
  const classes = ['dbs-rich-text-wrapper', className].filter(Boolean).join(' ');
  // Sanitize before innerHTML — same approach as FormatHtmlTextToReact / IconTips.
  return <div className={classes} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(value) }} />;
};

export const renderLiRichText = (value: string, idx: any, className?: string) => {
  if (!value || value.trim() === '') {
    return <li key={idx}>-</li>;
  }
  const classes = ['dbs-rich-text-wrapper', className].filter(Boolean).join(' ');
  return (
    <li
      key={idx}
      className={classes}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(value) }}
    />
  );
};

/**
 * Renders plain text by converting newline characters (\r\n, \n, \r) to <br/> tags.
 * Use this for non-rich-text values (e.g. table cell data that contains raw newlines).
 * @param value - The plain text string to render
 * @param className - Optional CSS class name to apply to the wrapper span
 * @returns React element with newlines converted to <br/>, or a dash for empty values
 */
export const renderNewlineToBr = (value?: string, className?: string) => {
  if (!value || value.trim() === '') {
    return <span>-</span>;
  }
  const html = value.replace(/(?:\r\n|\n|\r)/g, '<br/>');
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }}
    />
  );
};

// 过滤富文本中，字体大小的历史数据,
// 去除datas对象里的current_edit和last_edit两数组里的每个元素里issue和bos内容里面的font-size属性，
// 例如font-size: 10.0pt;，其他的全部保留
export const filterFontSize = (datas: any) => {
  if (!datas || typeof datas !== 'object') return datas;
  const removeFontSize = (str: string) => {
    if (!str || typeof str !== 'string') return str;
    return str.replace(/font-size\s*:\s*[^;]*;?\s*/gi, '');
  };
  const processItem = (item: any) => {
    if (!item || typeof item !== 'object') return item;
    const newItem = { ...item };
    if (newItem.issue !== null && newItem.issue !== undefined) newItem.issue = removeFontSize(String(newItem.issue));
    if (newItem.bos !== null && newItem.bos !== undefined) newItem.bos = removeFontSize(String(newItem.bos));
    return newItem;
  };
  const newDatas = { ...datas };
  if (Array.isArray(newDatas.current_edit)) {
    newDatas.current_edit = newDatas.current_edit.map(processItem);
  }
  if (Array.isArray(newDatas.last_edit)) {
    newDatas.last_edit = newDatas.last_edit.map(processItem);
  }
  return newDatas;
};

// 过滤富文本中，字体大小的历史数据,
// 去除datasList数组里的每个元素里description内容里面的font-size和background-color属性，
// 例如font-size: 10.0pt;background-color:rgb(222,222,222);其他的全部保留
export const filterFontSizeReview = (datasList: any) => {
  if (!Array.isArray(datasList)) return datasList;
  const removeFontSize = (str: string) => {
    if (!str || typeof str !== 'string') return str;
    return str.replace(/(?:font-size|background-color)\s*:\s*[^;]*;?\s*/gi, '');
  };
  return datasList.map((item: any) => {
    if (!item || typeof item !== 'object') return item;
    const newItem = { ...item };
    if (newItem.description !== null && newItem.description !== undefined) {
      newItem.description = removeFontSize(String(newItem.description));
    }
    return newItem;
  });
};

// 过滤富文本中，字体大小的历史数据,与背景颜色、字体，但要根据传入的key键列表来过滤
// 支持两种入参类型：
//   1. 传入字符串时：直接对该字符串进行过滤并返回过滤后的字符串
//   2. 传入数组时：对数组中对象的指定keyList字段内容进行过滤
// 过滤属性包括font-size、font-family、background、background-color，
// 例如font-size: 10.0pt;font-family: Menlo;background: none 0% 0%;background-color:rgb(222,222,222);其他的全部保留
// 同时过滤 h1 到 h5 的标题元素（含元素内容）
export const filterFontSizeReviewByKey = (data: any, keyList: string[] = ['description']): any => {
  const removeFontSize = (str: string) => {
    if (!str || typeof str !== 'string') return str;
    // 匹配 font-size、font-family、background、background-color 属性并移除
    return (
      str
        .replace(/(?:font-size|font-family|background(?:-color)?)\s*:\s*[^;]*;?\s*/gi, '')
        // 移除 h1 到 h5 的标题元素（含元素内容）
        .replace(/<h[1-5][^>]*>[\s\S]*?<\/h[1-5]>/gi, '')
    );
  };
  // 如果传入的是字符串，直接过滤该字符串
  if (typeof data === 'string') {
    return removeFontSize(data);
  }
  // 如果传入的是数组，遍历数组对每个对象中指定的字段进行过滤
  if (!Array.isArray(data)) return data;
  return data.map((item: any) => {
    if (!item || typeof item !== 'object') return item;
    const newItem = { ...item };
    // 遍历传入的 keyList 数组，对每个字段进行过滤
    keyList.forEach((key: string) => {
      if (newItem[key] !== null && newItem[key] !== undefined) {
        newItem[key] = removeFontSize(String(newItem[key]));
      }
    });
    return newItem;
  });
};
